import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Menu, X, Phone, Mail, Home, Wrench, Database, Globe,
  ShoppingCart, Users, Building2, MessageCircle, Headphones,
  Monitor, Server, Search, Cctv, Cpu, ChevronDown, Star, Newspaper,
  ShieldCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import SearchModal from './SearchModal';

const Navbar = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled]     = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [openMenu, setOpenMenu]         = useState(null); // desktop dropdown aberto
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const closeTimer = useRef(null);
  const btnRefs = useRef({});
  const [ddPos, setDdPos] = useState(null); // posição fixa do dropdown (portal)

  const positionDropdown = (name) => {
    const el = btnRefs.current[name];
    if (!el) return;
    const r = el.getBoundingClientRect();
    setDdPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
  };

  // Fecha dropdown ao rolar/redimensionar ou clicar fora
  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    const onDown = (e) => {
      if (e.target.closest('[data-dropdown-portal]') || e.target.closest('[data-nav-trigger]')) return;
      close();
    };
    window.addEventListener('scroll', close, { passive: true });
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onDown);
    };
  }, [openMenu]);

  // ── Scroll detection ──────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Ctrl+K / Cmd+K ────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Lock body scroll when mobile menu is open ─────────────────────────────
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // ── Navegação: âncora na home OU rota de página ────────────────────────────
  const go = (href) => {
    setIsMobileMenuOpen(false);
    setOpenMenu(null);
    setOpenMobileGroup(null);
    if (!href) return;
    if (href.startsWith('http')) { window.open(href, '_blank'); return; }
    // Rota de página inteira (ex.: /daten, /avaliacoes, /blog)
    if (href.startsWith('/') && !href.startsWith('/#')) {
      // Se a rota for a home com âncora
      navigate(href);
      return;
    }
    // Âncora: se não estiver na home, vai para home + âncora
    const anchor = href.replace('/#', '#');
    if (window.location.pathname !== '/') {
      navigate('/');
      let tries = 0;
      const tryScroll = () => {
        const el = document.querySelector(anchor);
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 120;
          window.scrollTo({ top, behavior: 'smooth' });
        } else if (tries++ < 40) {
          setTimeout(tryScroll, 150);
        }
      };
      setTimeout(tryScroll, 200);
      return;
    }
    const el = document.querySelector(anchor);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // ── Estrutura do menu simplificado com submenus ────────────────────────────
  const MENU = [
    { name: 'Início', href: '#home', icon: <Home className="w-4 h-4" /> },
    {
      name: 'Serviços', icon: <Wrench className="w-4 h-4" />,
      children: [
        { name: 'Serviços de TI',     href: '/servicos-ti',               desc: 'Suporte e infraestrutura' },
        { name: 'Assistência Técnica',href: '/assistencia-tecnica',       desc: 'Manutenção de PCs e notebooks' },
        { name: 'Suporte Remoto',     href: '#suporte-remoto',            desc: 'Atendimento à distância' },
        { name: 'Sites & IA',         href: '/sites',                     desc: 'Criação de sites com IA' },
        { name: 'Suporte Nacional',   href: '/suporte-remoto-nacional',   desc: 'Atendemos todo o Brasil' },
      ],
    },
    {
      name: 'Sistemas', icon: <Database className="w-4 h-4" />,
      children: [
        { name: 'Bling ERP',     href: '/bling',         desc: 'Parceiro oficial Bling' },
        { name: 'SHOficina ERP', href: '/shoficina',     desc: 'Sistema para oficinas' },
      ],
    },
    {
      name: 'Segurança', icon: <ShieldCheck className="w-4 h-4" />,
      children: [
        { name: 'Câmeras Intelbras',  href: '/intelbras',           desc: 'CFTV e segurança eletrônica' },
        { name: 'Orçamento Intelbras',href: '/orcamento-intelbras', desc: 'Solicite seu orçamento' },
      ],
    },
    {
      name: 'Empresa', icon: <Building2 className="w-4 h-4" />,
      children: [
        { name: 'Quem Somos',   href: '/quem-somos',   desc: 'Nossa história' },
        { name: 'Clientes',     href: '#clientes',     desc: 'Quem confia na gente' },
        { name: 'Clientes VIP', href: '/clientes-vip', desc: 'Empresas parceiras' },
        { name: 'Avaliações',   href: '/avaliacoes',   desc: 'Nota 5,0 no Google', icon: <Star className="w-3.5 h-3.5" /> },
      ],
    },
    {
      name: 'Loja', icon: <ShoppingCart className="w-4 h-4" />,
      children: [
        { name: 'Loja Virtual', href: '#loja-virtual', desc: 'Produtos e licenças' },
        { name: 'Comprar',      href: '/comprar',      desc: 'Finalizar pedido' },
        { name: 'Hospedagem',   href: '/hospedagem',   desc: 'Sites e e-mails' },
        { name: 'Daten',        href: '/daten',        desc: 'Computadores Daten' },
      ],
    },
    { name: 'Contato', href: '#contato', icon: <MessageCircle className="w-4 h-4" /> },
    { name: 'Blog',    href: '/blog',    icon: <Newspaper className="w-4 h-4" /> },
  ];

  const handleEnter = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    positionDropdown(name);
    setOpenMenu(name);
  };
  const toggleMenu = (name) => {
    if (openMenu === name) { setOpenMenu(null); return; }
    positionDropdown(name);
    setOpenMenu(name);
  };
  const activeItem = MENU.find(i => i.name === openMenu && i.children);
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 overflow-x-hidden ${
          isScrolled
            ? 'bg-slate-950/95 backdrop-blur-xl shadow-xl shadow-black/50'
            : 'bg-slate-950/70 backdrop-blur-xl'
        }`}
        style={{ maxWidth: '100vw', borderBottom: isScrolled ? '1px solid rgba(6,182,212,0.25)' : '1px solid rgba(6,182,212,0.12)' }}
      >
        {/* ── Strip: contato (md+) ─────────────────────────────────────────── */}
        <div className="hidden md:block border-b border-white/8 overflow-hidden">
          <div className="w-full max-w-full px-4 py-1.5 flex justify-end items-center gap-4 text-blue-200/80 text-sm overflow-hidden">
            <a href="tel:16991378312" className="flex items-center gap-1.5 hover:text-cyan-300 transition-colors shrink-0">
              <Phone size={12} /> (16) 99137-8312
            </a>
            <a href="mailto:contato@fernandotech.com.br" className="flex items-center gap-1.5 hover:text-cyan-300 transition-colors min-w-0">
              <Mail size={12} /> <span className="truncate">contato@fernandotech.com.br</span>
            </a>
          </div>
        </div>

        {/* ── Linha 1: Logo + Busca + Botões de ação ──────────────────────── */}
        <div className="w-full max-w-full px-4 py-2 overflow-hidden">
          <div className="flex items-center justify-between gap-3 min-w-0">
            {/* Logo */}
            <a href="#home" onClick={e => { e.preventDefault(); go('#home'); }} className="flex items-center gap-2.5 shrink-0">
              <img
                src="./fernando-tech-logo.png"
                alt="Fernando Tech"
                className="h-10 w-10 md:h-12 md:w-12 object-contain rounded-lg"
              />
              <div className="flex flex-col leading-tight">
                <span className="text-white font-bold text-base md:text-lg">Fernando Tech</span>
                <span className="text-blue-300 text-[10px]">Suporte Premium</span>
              </div>
            </a>

            {/* ── Desktop: Busca centralizada + botões ── */}
            <div className="hidden xl:flex items-center flex-1 gap-3 px-2">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="flex-1 max-w-2xl mx-auto flex items-center gap-3 px-5 py-2.5 relative rounded-full transition-all duration-300 group overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.8) 100%)',
                  border: '1.5px solid rgba(100,116,139,0.4)',
                  boxShadow: '0 0 0 0 rgba(6,182,212,0)',
                }}
                onMouseEnter={e => { e.currentTarget.style.border = '1.5px solid rgba(6,182,212,0.6)'; e.currentTarget.style.boxShadow = '0 0 18px 2px rgba(6,182,212,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.border = '1.5px solid rgba(100,116,139,0.4)'; e.currentTarget.style.boxShadow = '0 0 0 0 rgba(6,182,212,0)'; }}
                title="Pesquisar (Ctrl+K)"
                data-testid="search-bar-desktop"
              >
                <span className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <Search className="w-4 h-4 text-cyan-400/80 group-hover:text-cyan-400 transition-colors flex-shrink-0 relative z-10" />
                <span className="flex-1 text-left text-slate-500 text-sm group-hover:text-slate-300 transition-colors relative z-10 truncate">
                  Pesquise por serviços, assistência, câmeras, SHOficina...
                </span>
                <div className="flex items-center gap-1 relative z-10 flex-shrink-0">
                  <kbd className="text-[10px] text-slate-600 bg-slate-900/80 border border-slate-700 rounded px-1.5 py-0.5 font-mono hidden xl:inline">Ctrl</kbd>
                  <kbd className="text-[10px] text-slate-600 bg-slate-900/80 border border-slate-700 rounded px-1.5 py-0.5 font-mono hidden xl:inline">K</kbd>
                </div>
              </button>

              <Button
                onClick={() => go('#shoficina-erp')}
                className="bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white font-bold px-4 py-2 shadow-lg shadow-green-500/20 flex items-center gap-1.5 text-sm shrink-0"
                data-testid="navbar-shoficina-btn"
              >
                <Monitor className="w-4 h-4" /> SHOficina
              </Button>
              <Button
                onClick={() => go('#suporte-remoto')}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-bold px-4 py-2 shadow-lg shadow-blue-500/20 flex items-center gap-1.5 text-sm shrink-0"
                data-testid="navbar-suporte-btn"
              >
                <Headphones className="w-4 h-4" /> Suporte
              </Button>
            </div>

            {/* ── Mobile: ícones direita ── */}
            <div className="xl:hidden flex items-center gap-2">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="text-slate-300 p-2 hover:bg-blue-900/50 rounded-lg transition-colors"
                title="Pesquisar"
                data-testid="search-btn-mobile"
              >
                <Search size={20} />
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                className="text-white p-2 hover:bg-blue-900/50 rounded-lg transition-colors"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* ── Linha 2: Menu simplificado com submenus (xl+) ───────────────── */}
        <div className="hidden xl:block border-t border-slate-700/30 bg-slate-900/40 overflow-visible relative z-40">
          <div className="w-full max-w-full px-4">
            <div className="flex items-center justify-center gap-1">
              {MENU.map(item => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.children && handleEnter(item.name)}
                  onMouseLeave={() => item.children && handleLeave()}
                >
                  <button
                    ref={el => { btnRefs.current[item.name] = el; }}
                    data-nav-trigger
                    onClick={() => item.children ? toggleMenu(item.name) : go(item.href)}
                    className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg transition-all duration-200 font-medium text-[13px] whitespace-nowrap ${
                      openMenu === item.name ? 'text-cyan-400 bg-white/5' : 'text-gray-300 hover:text-cyan-400 hover:bg-white/5'
                    }`}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                  >
                    <span className="text-cyan-400/70">{item.icon}</span>
                    {item.name}
                    {item.children && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === item.name ? 'rotate-180' : ''}`} />}
                  </button>

                </div>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* ── Dropdown desktop via portal (fora do nav com overflow-hidden) ── */}
      {activeItem && ddPos && createPortal(
        <div
          data-dropdown-portal
          onMouseEnter={() => { if (closeTimer.current) clearTimeout(closeTimer.current); }}
          onMouseLeave={handleLeave}
          style={{ position: 'fixed', top: ddPos.top, left: ddPos.left, transform: 'translateX(-50%)', zIndex: 10000 }}
          className="hidden xl:block w-64 bg-slate-900 border border-cyan-500/20 rounded-2xl shadow-2xl shadow-black/60 p-2 animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid={`dropdown-${activeItem.name.toLowerCase()}`}
        >
          {activeItem.children.map(child => (
            <button
              key={child.name}
              onClick={() => go(child.href)}
              className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-cyan-500/10 group transition-colors"
              data-testid={`nav-item-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="mt-0.5 text-cyan-400/70 group-hover:text-cyan-400">{child.icon || activeItem.icon}</span>
              <span className="min-w-0">
                <span className="block text-white text-sm font-semibold group-hover:text-cyan-300">{child.name}</span>
                {child.desc && <span className="block text-slate-500 text-xs leading-tight">{child.desc}</span>}
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* ── Mobile Menu ──────────────────────────────────────────────────────── */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] xl:hidden" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/70" onClick={() => setIsMobileMenuOpen(false)} />
          <div
            className="absolute top-0 left-0 right-0 bg-slate-900 max-h-[92vh] overflow-y-auto shadow-2xl border-b border-cyan-500/30"
            onClick={e => e.stopPropagation()}
            style={{ touchAction: 'manipulation' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
              <a href="#home" onClick={e => { e.preventDefault(); go('#home'); }} className="flex items-center gap-3">
                <img src="./fernando-tech-logo.png" alt="Fernando Tech" className="h-10 w-10 object-contain rounded-lg" />
                <div className="flex flex-col">
                  <span className="text-white font-bold text-lg leading-tight">Fernando Tech</span>
                  <span className="text-blue-300 text-xs">Suporte Premium</span>
                </div>
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white p-2 hover:bg-slate-800 rounded-lg"
                data-testid="mobile-menu-close"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-5">
              {/* CTA Buttons */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Button
                  onClick={() => go('#shoficina-erp')}
                  className="bg-gradient-to-r from-green-600 to-emerald-500 text-white font-bold flex items-center justify-center gap-2 text-sm py-4 rounded-xl"
                  data-testid="navbar-shoficina-mobile-btn"
                >
                  <Monitor className="w-5 h-5" /> SHOficina
                </Button>
                <Button
                  onClick={() => go('#suporte-remoto')}
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold flex items-center justify-center gap-2 text-sm py-4 rounded-xl"
                >
                  <Headphones className="w-5 h-5" /> Suporte
                </Button>
              </div>

              <div className="border-t border-slate-700/50 my-4" />

              {/* Menu com acordeão de submenus */}
              <div className="flex flex-col gap-1">
                {MENU.map(item => (
                  <div key={item.name}>
                    <button
                      onClick={() => item.children ? setOpenMobileGroup(openMobileGroup === item.name ? null : item.name) : go(item.href)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 text-gray-100 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-xl font-semibold text-sm border border-slate-700/50 transition-all"
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="text-cyan-400 p-1.5 bg-slate-800/70 rounded-lg">{item.icon}</span>
                        {item.name}
                      </span>
                      {item.children && <ChevronDown className={`w-4 h-4 transition-transform ${openMobileGroup === item.name ? 'rotate-180' : ''}`} />}
                    </button>
                    {item.children && openMobileGroup === item.name && (
                      <div className="mt-1 ml-4 pl-3 border-l border-slate-700/60 flex flex-col gap-0.5">
                        {item.children.map(child => (
                          <button
                            key={child.name}
                            onClick={() => go(child.href)}
                            className="w-full text-left px-3 py-2.5 text-slate-300 hover:text-cyan-300 hover:bg-cyan-500/5 rounded-lg text-sm transition-colors flex items-center gap-2"
                            data-testid={`mobile-nav-item-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {child.icon && <span className="text-cyan-400/70">{child.icon}</span>}
                            {child.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-700/50 my-4" />

              {/* Search */}
              <button
                onClick={() => { setIsMobileMenuOpen(false); setIsSearchOpen(true); }}
                className="w-full flex items-center gap-3 px-5 py-3.5 bg-slate-800/60 border border-slate-700/50 hover:border-cyan-500/40 rounded-full text-slate-400 hover:text-white transition-all group mb-3"
                data-testid="search-btn-mobile-menu"
              >
                <Search className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 flex-shrink-0 transition-colors" />
                <span className="flex-1 text-left text-sm text-slate-500 group-hover:text-slate-300 transition-colors">
                  Pesquisar serviços, produtos...
                </span>
                <kbd className="text-[10px] text-slate-600 bg-slate-900/60 border border-slate-700 rounded px-1.5 py-0.5 font-mono">Ctrl K</kbd>
              </button>

              <div className="border-t border-slate-700/50 my-4" />

              {/* Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <a
                  href="tel:16991378312"
                  className="flex items-center justify-center gap-3 text-white hover:text-cyan-400 text-sm font-semibold bg-slate-800 hover:bg-cyan-900/50 py-3 px-4 rounded-xl border border-slate-600/50 hover:border-cyan-500/50 transition-all"
                >
                  <Phone size={18} className="text-green-400" /> (16) 99137-8312
                </a>
                <a
                  href="mailto:contato@fernandotech.com.br"
                  className="flex items-center justify-center gap-3 text-white hover:text-cyan-400 text-sm font-semibold bg-slate-800 hover:bg-cyan-900/50 py-3 px-4 rounded-xl border border-slate-600/50 hover:border-cyan-500/50 transition-all"
                >
                  <Mail size={18} className="text-blue-400" />
                  <span className="truncate">contato@fernandotech.com.br</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Navbar;
